import { checkCognitoSession } from './userPool'
import jsTypes from '../constants/jsTypes'

/**
 * After the returned promise resolves, you probably want to call .json() on it to get the JSON value of the response body:
 *   https://developer.mozilla.org/en-US/docs/Web/API/Response
 *
 * Also, .status gives the integer of the response's statusCode.
 * @returns {Promise<Response>}
 */
export const callHttpAsync = ({ path = '', body, method = 'GET', headers = {}, queryParameters }) => {
  const apiBasePath = API_BASE_PATH // believe this comes from dotenv via webpack, it's defined in customer-portal/env/{stageName}.txt
  const fullPath = apiBasePath + path + formatQueryParameters(queryParameters)
  const session = checkCognitoSession()
  const token = window.sessionStorage.getItem('token')
  const idToken = session
    ? session.getIdToken().getJwtToken()
    : undefined

  headers.Authorization = `Bearer ${idToken}`
  if (!idToken) headers.token = token

  if (!headers['Content-Type']) headers['Content-Type'] = 'application/json'

  // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
  return fetch(fullPath, {
    method,
    mode: 'cors',
    cache: 'default',
    credentials: 'same-origin',
    headers,
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: body ? JSON.stringify(body) : undefined
  })
}

export const getJsonResponse = async ({ path = '', body, method = 'GET', headers = {}, queryParameters }) => {
  const response = await callHttpAsync({ path, body, method, headers, queryParameters })
  const statusCode = response.status
  let responseBody
  try {
    responseBody = await response.json()
  } catch (e) {
    responseBody = {}
  }

  return {
    statusCode,
    body: responseBody
  }
}

export const getJsonResponseAsync = ({ path = '', body = {}, method = 'GET', headers = {}, queryParameters }) => {
  return new Promise((resolve, reject) => {
    try {
      callHttpAsync({ path, body, method, headers, queryParameters })
        .then((response) => {
          const statusCode = response.status
          response.json().then((responseBody) => {
            resolve({
              statusCode,
              body: JSON.parse(responseBody || '{}')
            })
          })
        })
    } catch (error) {
      reject(error)
    }
  })
}

const formatQueryParameters = (queryParameters) => (queryParameters
  ? `?${Object.entries(queryParameters)
    .map(([key, value]) => `${encodeURIComponent(key)}=${formatQueryParameterValue(value)}`)
    .join('&')}`
  : '')


const formatQueryParameterValue = (value) => {
  switch (typeof value) {
    case jsTypes.FUNCTION:
    case jsTypes.SYMBOL:
      throw new Error('bad query parameter type')
    case jsTypes.STRING:
      return encodeURIComponent(value)
    case jsTypes.BIGINT:
    case jsTypes.NUMBER:
    case jsTypes.BOOLEAN:
      return value.toString()
    case jsTypes.OBJECT:
      if (Array.isArray(value)) return value.join()
      if (value === null) return ''
      return JSON.stringify(value)
    case jsTypes.UNDEFINED:
      return ''
  }
}
